import React from 'react';

import { Button, Group, Modal, Stack, Text, Title } from '@mantine/core';
import { AxiosError, AxiosResponse } from 'axios';
import { useAuth } from 'context/auth-context';
import { useLastCreditAlert } from 'context/last-credit-alert-context';
import useCancelAthleteAppointment from 'hooks/athleteAppointment/useCancelAthleteAppointment';
import useAthletePackageAllLimitedCreditsUsed from 'hooks/athletePackage/useAthletePackageAllLimitedCreditsUsed';
import useLastCreditAlertBuilder from 'hooks/athletePackage/useLastCreditAlertBuilder';
import { useAthleteTracking } from 'hooks/mixpanel/useAthleteTracking';
import { UseMutationResult } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { AppointmentAthleteRetrieve, AthleteAppointment, ErrorMessage } from 'types';
import notificationHandler from 'utils/notifications';

interface IModalAthleteAppointmentDelete {
  opened: boolean;
  handleClose: () => void;
  athleteAppointmentId: string;
  appointment: AppointmentAthleteRetrieve;
  deleteAthleteAppointmentMutation: UseMutationResult<
    AxiosResponse<null | AthleteAppointment>,
    AxiosError<ErrorMessage>,
    string
  >;
}

const ModalAthleteAppointmentDelete = ({
  opened,
  handleClose,
  athleteAppointmentId,
  appointment,
}: IModalAthleteAppointmentDelete): JSX.Element => {
  const navigate = useNavigate();
  const { successNotification, errorNotification } = notificationHandler();

  const { session } = useAuth();
  const athleteTracking = useAthleteTracking();
  const deleteAthleteAppointment = useCancelAthleteAppointment();
  const getAthletePackageAllLimitedCreditsUsed = useAthletePackageAllLimitedCreditsUsed();
  const buildLastCreditAlert = useLastCreditAlertBuilder('athlete');
  const { showRenewalNotification } = useLastCreditAlert();

  const creditDeduction = appointment?.cancellationSettings.cancellationCreditDeduction || false;
  const lateFee = appointment.cancellationSettings.cancellationFee || 0;

  const handleDelete = (): void => {
    if (athleteAppointmentId) {
      deleteAthleteAppointment.mutate(athleteAppointmentId, {
        onSuccess: response => {
          successNotification('Athlete reservation cancel success', `Reservation successfully canceled.`);
          handleClose();

          athleteTracking.trackAppointmentCancelled(
            appointment.appointmentId,
            appointment.appointmentType.name,
            appointment.consideredLateCancel,
            creditDeduction,
            lateFee,
          );

          if (response.data?.creditDispensation && response?.data.isLateCancel) {
            getAthletePackageAllLimitedCreditsUsed.mutate(
              { creditDispensation: response.data.creditDispensation },
              {
                onSuccess: res => {
                  const alert = buildLastCreditAlert(res.data, session?.athlete?.firstName ?? '');
                  if (alert) {
                    showRenewalNotification(alert);
                  }
                },
                onError: error => {
                  errorNotification(
                    'Error to check if all limited credits used',
                    error.response?.data.message,
                  );
                },
              },
            );
          }

          // Navigate back to previous page
          navigate(-1);
        },
        onError: error => {
          errorNotification('Athlete reservation cancel failed', error.response?.data.message);
        },
      });
    }
  };
  return (
    <Modal
      centered
      opened={opened}
      onClose={handleClose}
      title={<Title order={4}>Cancel reservation</Title>}
      size='md'
    >
      <Stack>
        {!appointment.consideredLateCancel && (
          <Text>{`Are you sure you want to cancel this reservation?`}</Text>
        )}
        {appointment.consideredLateCancel && lateFee === 0 && creditDeduction && (
          <Text>
            {`The cancellation window has closed. If you attempt to cancel your reservation now, a credit will
            be deducted from your package. Do you want to continue to cancel your reservation?`}
          </Text>
        )}
        {appointment.consideredLateCancel && lateFee > 0 && creditDeduction && (
          <Text>
            {`The cancellation window has closed. If you attempt to cancel your reservation now, a credit will
            be deducted from your package and you will be charged a fee. Do you want to continue to cancel
            your reservation?`}
          </Text>
        )}
        {appointment.consideredLateCancel && !creditDeduction && lateFee === 0 && (
          <Text>
            {`The cancellation window has closed. There is no fee or credit usage for cancelling this
            reservation. Do you want to continue to cancel your reservation?`}
          </Text>
        )}
        {appointment.consideredLateCancel && !creditDeduction && lateFee > 0 && (
          <Text>
            {`The cancellation window has closed. You will be charged a fee for cancelling this reservation but
            there is no credit deduction. Do you want to continue to cancel your reservation?`}
          </Text>
        )}
        <Group position='center' spacing='md' mt='md'>
          <Button onClick={handleDelete} color='red' fullWidth loading={deleteAthleteAppointment.isLoading}>
            Cancel Reservation
          </Button>
          <Button
            onClick={handleClose}
            variant='outline'
            fullWidth
            color='dark'
            disabled={deleteAthleteAppointment.isLoading}
          >
            Keep Reservation
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ModalAthleteAppointmentDelete;
